/**
 * PopupRoutesPage - Stateless routes/port arrivals viewer popup
 *
 * Receives all data from the main window via BroadcastChannel.
 * Does NOT create its own WebSocket connections or API calls.
 *
 * Uses the "stale while revalidate" pattern - shows existing data
 * with a refresh indicator while new data is being fetched.
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from 'react';
import {
  Button,
  Card,
  LoadingSpinner,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Command,
  CommandGroup,
  CommandItem,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@ais-ui/ui-core';
import { useLocalization } from '@ais-ui/locale-management';
import { useDateTimeFormat } from '../conversions/useDateFormat';
import { ChevronDown, X } from 'lucide-react';
import { PiAnchor, PiFileCsv } from 'react-icons/pi';
import {
  PopupType,
  getPopupIdFromUrl,
  getPopupParams,
  closePopup,
} from '@ais-ui/popups';
import {
  PopupActions,
  MessageType,
  useMainWindowMessages,
  PopupMessage,
} from './PopupEvents';
import { usePopupLifecycle } from './usePopupLifecycle';
import { PopupConnectionStatus } from './shared/PopupConnectionStatus';
import { usePopupPerformanceTracking } from './usePopupPerformanceTracking';
import { usePopupDataState } from './usePopupDataState';
import { PopupRefreshIndicator } from './PopupRefreshIndicator';
import { PopupDistributionChart, getDensityClasses } from './shared';
import { TrackResult } from '@ais-ui/api-spec/portserver';
import { PortTimeline } from './PortTimeline';
import { EnrichedNextPortPrediction } from './ArrivalsDataProvider';
import { RoutesNextPortTab } from './RoutesNextPortTab';
import { RoutesArrivalsTable } from './RoutesArrivalsTable';

/**
 * Data structure sent from main window
 */
export interface PopupRoutesData {
  arrivals: TrackResult[];
  portId?: number;
  portName?: string;
  portLocode?: string;
  shapeId?: string;
  shapeName?: string;
  settings?: {
    tableDensity: 'normal' | 'compact';
  };
  error?: string;
  /** Unique key for this data snapshot - used to prevent unnecessary re-renders */
  dataKey?: string;
  /** NextPort arrival predictions (enriched with vessel info) */
  nextportPredictions?: EnrichedNextPortPrediction[];
  /** Permissions passed from main window */
  permissions?: {
    nextport?: boolean;
  };
}

const ROUTES_CHART_OPTIONS = [
  { value: 'country', label: 'Country' },
  { value: 'shiptype', label: 'Ship Type' },
  { value: 'state', label: 'State' },
];

export const PopupRoutesPage: React.FC = () => {
  const popupId = getPopupIdFromUrl();
  const params = getPopupParams();
  const { formatDateTimeWithTimeZone } = useDateTimeFormat();
  const { messages } = useLocalization();

  // Use the popup data state hook for "stale while revalidate" pattern
  const {
    data,
    isInitialLoading,
    isRefreshing,
    error,
    setData,
    setRefreshing,
    setError,
  } = usePopupDataState<PopupRoutesData>();

  const [lastFetched, setLastFetched] = useState<Date | null>(null);
  const dataReceivedRef = useRef(false);
  const dataRef = useRef<PopupRoutesData | null>(null);
  const dataKeyRef = useRef<string | undefined>(undefined);

  // Keep dataRef in sync with data
  dataRef.current = data;

  // Memoize metadata to prevent performance tracking from re-creating callbacks
  const trackingMetadata = useMemo(
    () => ({
      portLocode: params.portLocode || params.locode || 'unknown',
      shapeId: params.shapeId || 'none',
    }),
    [params.portLocode, params.locode, params.shapeId],
  );

  // Performance tracking
  const { trackDataRequest, trackDataReceived, trackError, trackTimeout } =
    usePopupPerformanceTracking({
      popupId,
      popupType: PopupType.Routes,
      metadata: trackingMetadata,
    });

  // Table density from settings (default to compact)
  const tableDensity = data?.settings?.tableDensity || 'compact';
  const densityClasses = getDensityClasses(tableDensity);

  // Sorting state
  const [sortColumn, setSortColumn] = useState<string>('name');
  const [sortAscending, setSortAscending] = useState(true);

  // Chart state
  const [chartGroupBy, setChartGroupBy] = useState('country');
  // Default to 'chart' unless URL param specifies 'timeline' or 'nextport'
  const [activeTab, setActiveTab] = useState(() => {
    if (params.activeTab === 'timeline') return 'timeline';
    if (params.activeTab === 'nextport' || params.initialTab === 'nextport')
      return 'nextport';
    return 'chart';
  });

  // NextPort tab state
  const [nextportSearch, setNextportSearch] = useState('');
  const [nextportSortColumn, setNextportSortColumn] = useState<
    'name' | 'eta' | 'probability'
  >('eta');
  const [nextportSortAscending, setNextportSortAscending] = useState(true);

  // Request initial data from main window
  useEffect(() => {
    if (!popupId) {
      setError('No popup ID found');
      trackError('No popup ID found');
      return;
    }

    dataReceivedRef.current = false;
    trackDataRequest();

    PopupActions.requestInit({
      popupId,
      popupType: PopupType.Routes,
      params,
    });

    const timeout = setTimeout(() => {
      if (!dataReceivedRef.current) {
        setError('Timeout waiting for data from main window.');
        trackTimeout();
      }
    }, 10000);

    return () => clearTimeout(timeout);
  }, [popupId, trackDataRequest, trackError, trackTimeout]);

  // Listen for messages from main window
  useMainWindowMessages(
    (msg: PopupMessage) => {
      if (
        msg.type === MessageType.POPUP_INIT_RESPONSE &&
        msg.popupId === popupId
      ) {
        const payload = msg.payload as PopupRoutesData;

        // Don't update if response has error and we already have data
        if (payload.error && dataRef.current?.arrivals?.length) {
          setRefreshing(false);
          return;
        }

        // Don't update with empty data if we already have data (stale is better than empty)
        if (
          dataRef.current?.arrivals?.length &&
          (!payload.arrivals || payload.arrivals.length === 0)
        ) {
          setRefreshing(false);
          return;
        }

        // Only update if dataKey is different to prevent unnecessary re-renders (flickering)
        if (payload.dataKey && payload.dataKey === dataKeyRef.current) {
          setRefreshing(false);
          return;
        }

        dataReceivedRef.current = true;
        dataKeyRef.current = payload.dataKey;
        setData(payload);
        setLastFetched(new Date());
        trackDataReceived(payload.arrivals?.length);
        document.title = payload.portName || payload.shapeName || 'Routes';
      }

      if (msg.type === MessageType.DATA_UPDATE && msg.popupId === popupId) {
        const payload = msg.payload as {
          dataType: string;
          data: PopupRoutesData | null;
          isLoading?: boolean;
        };
        if (payload.dataType === 'routes') {
          if (payload.isLoading) {
            // Data fetch started - show refresh indicator
            setRefreshing(true);
          } else if (payload.data) {
            // Don't update with empty data
            if (
              dataRef.current?.arrivals?.length &&
              (!payload.data.arrivals || payload.data.arrivals.length === 0)
            ) {
              setRefreshing(false);
              return;
            }
            // Only update if dataKey is different
            if (
              payload.data.dataKey &&
              payload.data.dataKey === dataKeyRef.current
            ) {
              setRefreshing(false);
              return;
            }
            dataKeyRef.current = payload.data.dataKey;
            setData(payload.data);
            setLastFetched(new Date());
          }
        }
      }

      if (msg.type === MessageType.SESSION_ENDED) {
        setError('Session ended. Please close this window.');
        trackError('Session ended');
      }
    },
    popupId ?? undefined,
    [popupId, setRefreshing, trackDataReceived, trackError],
  );

  // Handle popup lifecycle (close notification, heartbeat)
  const connectionState = usePopupLifecycle(popupId, PopupType.Routes);

  // Periodic refresh every 30 seconds to get updated arrivals
  // Using longer interval since portserver can be fragile
  useEffect(() => {
    if (!popupId || isInitialLoading) return;

    const interval = setInterval(() => {
      setRefreshing(true);
      PopupActions.requestInit({
        popupId,
        popupType: PopupType.Routes,
        params,
      });
    }, 30000);

    return () => clearInterval(interval);
  }, [popupId, isInitialLoading, params, setRefreshing]);

  // Sort arrivals client-side
  const sortedArrivals = useMemo(() => {
    if (!data?.arrivals) return [];

    return [...data.arrivals].sort((a, b) => {
      let aVal: any, bVal: any;

      switch (sortColumn) {
        case 'name':
          aVal = a.source?.ship?.name || '';
          bVal = b.source?.ship?.name || '';
          break;
        case 'mmsi':
          aVal = a.source?.ship?.mmsi || 0;
          bVal = b.source?.ship?.mmsi || 0;
          break;
        case 'callsign':
          aVal = a.source?.ship?.callsign || '';
          bVal = b.source?.ship?.callsign || '';
          break;
        case 'draught':
          aVal = a.source?.ship?.draught || a.source?.draught || 0;
          bVal = b.source?.ship?.draught || b.source?.draught || 0;
          break;
        case 'country':
          aVal = a.source?.ship?.country || '';
          bVal = b.source?.ship?.country || '';
          break;
        case 'eta':
          aVal = a.eta ? new Date(a.eta).getTime() : 0;
          bVal = b.eta ? new Date(b.eta).getTime() : 0;
          break;
        case 'etaErp':
          aVal = a.etaErp ? new Date(a.etaErp).getTime() : 0;
          bVal = b.etaErp ? new Date(b.etaErp).getTime() : 0;
          break;
        case 'state':
          aVal = a.message || '';
          bVal = b.message || '';
          break;
        case 'shiptype':
          aVal = a.source?.ship?.shiptype || '';
          bVal = b.source?.ship?.shiptype || '';
          break;
        default:
          aVal = '';
          bVal = '';
      }

      if (typeof aVal === 'string') {
        return sortAscending
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }
      return sortAscending ? aVal - bVal : bVal - aVal;
    });
  }, [data?.arrivals, sortColumn, sortAscending]);

  // Chart data
  const chartData = useMemo(() => {
    if (!data?.arrivals?.length) return [];

    const grouped: Record<string, number> = {};
    data.arrivals.forEach((arrival) => {
      let key = 'Unknown';
      switch (chartGroupBy) {
        case 'country':
          key = arrival.source?.ship?.country || 'Unknown';
          break;
        case 'shiptype':
          key = arrival.source?.ship?.shiptype || 'Unknown';
          break;
        case 'state':
          key = arrival.message || 'Unknown';
          break;
      }
      grouped[key] = (grouped[key] || 0) + 1;
    });

    return Object.entries(grouped)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 15);
  }, [data?.arrivals, chartGroupBy]);

  // Filtered and sorted NextPort predictions
  const filteredNextportPredictions = useMemo(() => {
    if (!data?.nextportPredictions) return [];

    let filtered = data.nextportPredictions;

    // Apply search filter (min 3 characters)
    if (nextportSearch.length >= 3) {
      const searchLower = nextportSearch.toLowerCase();
      filtered = filtered.filter((prediction) => {
        const name = prediction.name?.toLowerCase() || '';
        const imo = prediction.imo.toString();
        const mmsi = prediction.mmsi?.toString() || '';
        return (
          name.includes(searchLower) ||
          imo.includes(searchLower) ||
          mmsi.includes(searchLower)
        );
      });
    }

    // Sort
    return [...filtered].sort((a, b) => {
      let aVal: any, bVal: any;

      switch (nextportSortColumn) {
        case 'name':
          aVal = a.name || '';
          bVal = b.name || '';
          break;
        case 'eta':
          aVal = a.eta ? new Date(a.eta).getTime() : 0;
          bVal = b.eta ? new Date(b.eta).getTime() : 0;
          break;
        case 'probability':
          aVal = a.probability ?? 0;
          bVal = b.probability ?? 0;
          break;
        default:
          aVal = '';
          bVal = '';
      }

      if (typeof aVal === 'string') {
        return nextportSortAscending
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }
      return nextportSortAscending ? aVal - bVal : bVal - aVal;
    });
  }, [
    data?.nextportPredictions,
    nextportSearch,
    nextportSortColumn,
    nextportSortAscending,
  ]);

  const handleSort = useCallback(
    (column: string) => {
      if (sortColumn === column) {
        setSortAscending(!sortAscending);
      } else {
        setSortColumn(column);
        setSortAscending(true);
      }
    },
    [sortColumn, sortAscending],
  );

  const handleViewTarget = useCallback((arrival: TrackResult) => {
    const ship = arrival.source?.ship;
    if (!ship) return;

    PopupActions.viewTarget({
      name: ship.name || '',
      mmsi: ship.mmsi?.toString() || '',
      imo: ship.imo?.toString() || '',
      callSign: ship.callsign || '',
    });
  }, []);

  // NextPort action handlers
  const handleNextportSort = useCallback(
    (column: string) => {
      const col = column as 'name' | 'eta' | 'probability';
      if (nextportSortColumn === col) {
        setNextportSortAscending(!nextportSortAscending);
      } else {
        setNextportSortColumn(col);
        setNextportSortAscending(col !== 'probability'); // probability defaults descending
      }
    },
    [nextportSortColumn, nextportSortAscending],
  );

  const toTargetInfo = (prediction: EnrichedNextPortPrediction) => ({
    name: prediction.name || '',
    mmsi: prediction.mmsi?.toString() || '',
    imo: prediction.imo?.toString() || '',
  });

  const handleNextportViewTarget = useCallback(
    (prediction: EnrichedNextPortPrediction) => {
      PopupActions.viewTarget(toTargetInfo(prediction));
    },
    [],
  );

  const handleNextportZoomToTarget = useCallback(
    (prediction: EnrichedNextPortPrediction) => {
      PopupActions.zoomToTarget(toTargetInfo(prediction));
    },
    [],
  );

  const exportToCSV = useCallback(() => {
    if (!data?.arrivals) return;

    const headers = [
      'name',
      'mmsi',
      'imo',
      'callsign',
      'draught',
      'country',
      'eta_calculated',
      'state',
      'eta_erp',
    ];
    const formatValue = (v: unknown) => {
      if (v == null) return '';
      const s = String(v);
      return s.includes(',') || s.includes('"')
        ? `"${s.replace(/"/g, '""')}"`
        : s;
    };

    const csv = [
      headers.join(','),
      ...data.arrivals.map((a) =>
        [
          formatValue(a.source?.ship?.name),
          formatValue(a.source?.ship?.mmsi),
          formatValue(a.source?.ship?.imo),
          formatValue(a.source?.ship?.callsign),
          formatValue(a.source?.ship?.draught || a.source?.draught),
          formatValue(a.source?.ship?.country),
          formatValue(a.eta),
          formatValue(a.message),
          formatValue(a.etaErp),
        ].join(','),
      ),
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `routes_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [data?.arrivals]);

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-background">
        <div className="text-destructive text-lg font-medium mb-4">
          {messages.popupGenericError}
        </div>
        <p className="text-muted-foreground mb-4">{error}</p>
        <Button onClick={closePopup}>{messages.popupGenericCloseWindow}</Button>
      </div>
    );
  }

  if (isInitialLoading || !data) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-background">
        <LoadingSpinner className="h-8 w-8" />
        <p className="mt-4 text-muted-foreground">
          {messages.popupRoutesLoading}
        </p>
      </div>
    );
  }

  const title = data.portName || data.shapeName || messages.popupRoutesTitle;

  return (
    <div className="flex flex-col h-screen bg-gradient-to-b from-primary/5 via-background to-background relative">
      {/* Connection status overlay */}
      <PopupConnectionStatus connectionState={connectionState} />

      {/* Refresh indicator overlay - doesn't shift layout */}
      <PopupRefreshIndicator
        isRefreshing={isRefreshing}
        message={messages.popupGenericUpdatingData}
      />

      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b">
        <div className="flex items-center gap-2">
          <PiAnchor className="h-6 w-6 text-muted-foreground" />
          <div>
            <h1 className="text-xl font-semibold">{title}</h1>
            <p className="text-sm text-muted-foreground">
              {data.arrivals?.length ?? 0}{' '}
              {messages.popupRoutesVesselCount?.replace('{count}', '') ||
                'vessels'}
              {lastFetched && (
                <span className="ml-2">
                  •{' '}
                  {messages.popupRoutesUpdated?.replace(
                    '{time}',
                    formatDateTimeWithTimeZone(lastFetched),
                  )}
                </span>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="default">
                {messages.popupRoutesActions}{' '}
                <ChevronDown className="ml-2 h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-56">
              <Command>
                <CommandGroup>
                  <CommandItem onSelect={exportToCSV}>
                    <PiFileCsv className="mr-2 h-4 w-4" />{' '}
                    {messages.popupGenericExportCsv}
                  </CommandItem>
                </CommandGroup>
              </Command>
            </PopoverContent>
          </Popover>
          <Button variant="ghost" size="icon" onClick={closePopup}>
            <X className="h-5 w-5" />
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col overflow-hidden p-4">
        <Tabs
          value={activeTab}
          onValueChange={setActiveTab}
          className="flex-1 flex flex-col min-h-0"
        >
          <TabsList className="w-fit">
            <TabsTrigger value="chart">
              {messages.popupRoutesChartTab}
            </TabsTrigger>
            <TabsTrigger value="details">
              {messages.popupRoutesDetailsTab}
            </TabsTrigger>
            {data.portLocode && (
              <TabsTrigger value="timeline">
                {messages.popupRoutesPortCallTimeline}
              </TabsTrigger>
            )}
            {data.portLocode && data.permissions?.nextport && (
              <TabsTrigger value="nextport">
                {messages.popupRoutesNextportTab || 'NextPort'}
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="chart" className="mt-4">
            <PopupDistributionChart
              data={chartData}
              groupBy={chartGroupBy}
              onGroupByChange={setChartGroupBy}
              options={ROUTES_CHART_OPTIONS.map((opt) => ({
                value: opt.value,
                label:
                  messages[`popupRoutesChart${opt.label.replace(' ', '')}`] ||
                  opt.label,
              }))}
              title={messages.popupRoutesDistributionBy}
              noDataText={messages.popupRoutesChartNoData}
            />
          </TabsContent>

          <TabsContent value="details" className="mt-4">
            <Card className="p-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <span className="text-sm text-muted-foreground">
                    {messages.popupTargetsTotal}
                  </span>
                  <p className="text-lg font-medium">
                    {data.arrivals?.length ?? 0}
                  </p>
                </div>
                {data.portName && (
                  <div>
                    <span className="text-sm text-muted-foreground">Port</span>
                    <p className="text-lg font-medium">{data.portName}</p>
                  </div>
                )}
                {data.portLocode && (
                  <div>
                    <span className="text-sm text-muted-foreground">
                      Locode
                    </span>
                    <p className="text-lg font-medium">{data.portLocode}</p>
                  </div>
                )}
              </div>
            </Card>
          </TabsContent>

          {data.portLocode && (
            <TabsContent value="timeline" className="mt-4 flex-1 overflow-auto">
              <PortTimeline locode={data.portLocode} portName={data.portName} />
            </TabsContent>
          )}

          {/* NextPort predictions tab */}
          {data.portLocode && data.permissions?.nextport && (
            <TabsContent
              value="nextport"
              className="mt-4 flex-1 flex flex-col overflow-hidden min-h-0"
            >
              <RoutesNextPortTab
                predictions={filteredNextportPredictions}
                search={nextportSearch}
                onSearchChange={setNextportSearch}
                sortColumn={nextportSortColumn}
                sortAscending={nextportSortAscending}
                onSort={handleNextportSort}
                onViewTarget={handleNextportViewTarget}
                onZoomToTarget={handleNextportZoomToTarget}
                formatDateTime={formatDateTimeWithTimeZone}
                densityClasses={densityClasses}
                messages={messages}
              />
            </TabsContent>
          )}

          {/* Table - shown when not on timeline or nextport tab */}
          {activeTab !== 'timeline' && activeTab !== 'nextport' && (
            <RoutesArrivalsTable
              arrivals={sortedArrivals}
              sortColumn={sortColumn}
              sortAscending={sortAscending}
              onSort={handleSort}
              onViewTarget={handleViewTarget}
              formatDateTime={formatDateTimeWithTimeZone}
              densityClasses={densityClasses}
              messages={messages}
              totalCount={data.arrivals?.length ?? 0}
            />
          )}
        </Tabs>
      </div>
    </div>
  );
};

export default PopupRoutesPage;
